/**
 * Gráficos do módulo Pedidos, feitos à mão (sem biblioteca, para durar — D5).
 * Uma série só → uma cor (destaque Werc), sem legenda; o título nomeia a série.
 * Barras finas com ponta arredondada só no fim do dado; valores à direita;
 * dica ao passar o mouse com o detalhe. A tabela ao lado é a visão em texto.
 */
import { For, Show } from 'solid-js'
import { Tooltip } from '@ui/components/Tooltip'

export interface Barra {
  rotulo: string
  valor: number
  texto: string // valor formatado
  dica: string
}

/** Barras horizontais (ranking): rótulo · barra · valor. */
export function BarrasHorizontais(props: { titulo: string; barras: Barra[]; vazio?: string; aoClicar?: (i: number) => void; ativo?: number }) {
  const max = () => Math.max(1, ...props.barras.map((b) => b.valor))
  return (
    <figure class="flex flex-col gap-2">
      <figcaption class="text-xs font-medium uppercase tracking-wide text-ink-subtle">{props.titulo}</figcaption>
      <Show when={props.barras.length > 0} fallback={<p class="text-sm text-ink-placeholder">{props.vazio ?? 'Sem dados.'}</p>}>
        <ul class="flex flex-col">
          <For each={props.barras}>
            {(b, i) => (
              <li>
                <Tooltip label={b.dica} placement="top" class="block w-full">
                  <button
                    type="button"
                    class="w-full grid grid-cols-[minmax(5rem,min(38%,16rem))_minmax(3rem,1fr)_auto] items-center gap-3 py-1.5 px-1 rounded text-left hover:bg-hover"
                    classList={{ 'bg-hover': props.ativo === i(), 'cursor-default': !props.aoClicar }}
                    onClick={() => props.aoClicar?.(i())}
                  >
                    <span class="truncate text-[13px] text-ink" classList={{ 'font-medium': props.ativo === i() }}>{b.rotulo}</span>
                    <span class="h-2.5 rounded-r bg-accent" style={{ width: b.valor > 0 ? `${Math.max(1.5, (b.valor / max()) * 100)}%` : '0' }} />
                    <span class="text-[13px] tabular-nums text-ink-muted text-right min-w-16">{b.texto}</span>
                  </button>
                </Tooltip>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </figure>
  )
}

/** Colunas por ano (poucos anos → rótulo direto em cima de cada coluna). */
export function Colunas(props: { titulo: string; barras: Barra[] }) {
  const max = () => Math.max(1, ...props.barras.map((b) => b.valor))
  return (
    <figure class="flex flex-col gap-2">
      <figcaption class="text-xs font-medium uppercase tracking-wide text-ink-subtle">{props.titulo}</figcaption>
      <div class="flex items-end gap-3 h-44 border-b border-edge px-1">
        <For each={props.barras}>
          {(b) => (
            <Tooltip label={b.dica} placement="top" class="flex flex-1 h-full items-stretch">
              <div class="w-full h-full flex flex-col items-center gap-1 min-w-10">
                <span class="text-[11px] tabular-nums text-ink-muted whitespace-nowrap">{b.texto}</span>
                <div class="flex-1 w-full flex items-end justify-center">
                  <span class="w-full max-w-12 rounded-t bg-accent" style={{ height: `${Math.max(1, (b.valor / max()) * 100)}%` }} />
                </div>
              </div>
            </Tooltip>
          )}
        </For>
      </div>
      <div class="flex gap-3 px-1">
        <For each={props.barras}>{(b) => <span class="flex-1 min-w-10 text-center text-xs text-ink-subtle tabular-nums">{b.rotulo}</span>}</For>
      </div>
    </figure>
  )
}
